import { useEffect, useMemo } from 'react'
import { v4 as getUuid } from 'uuid'

import { viewProductList } from 'clients/segment'
import { formatPinnedProducts } from 'ssg/utils/format-pinned-products'
import mapEntityPositions from 'utils/mapEntityPositions'
import { pinEntities } from 'utils/pin-entities'
import { getSortedAndFilteredBySortPosition } from 'utils/sort/sortPosition'
import { useIsPinningActive } from 'utils/useIsPinningActive'
import { useIsUserOnBrave } from 'utils/useIsUserOnBrave'
import type { CompareCredit } from '../../../../types/compare-credit'
import ErrorBoundary from '../../error-boundary'
import { CardTile } from './card'
import { LoadingSkeleton } from '../tipCardTileList/loading-skeleton'
import { getIssuer } from '../utils'

type AugmentedCardTile = CompareCredit.CardTipAwards & {
  referencedCard: CompareCredit.Entity
  cardSlug: string
  cardIssuer: string
  linkText?: string
  sortPosition?: number
  cardSortContext?: string
}

const TipCardTileDefault: React.FC<{
  cardTile: AugmentedCardTile
  cardOrderIds: Record<string, string>
  referencedCards: null | Record<string, CompareCredit.Entity>
  tag: CompareCredit.CategoryTag
  categoryTag: CompareCredit.CategoryTag
  arrangementId?: string
  position: number
}> = ({ cardTile, cardOrderIds, tag, categoryTag, position }) => {
  const externalId = tag ? tag.id : '00'
  const categoryId = categoryTag ? categoryTag.id : '00'

  const { referencedCard, cardSlug, whyWeLikeIt } = cardTile

  const orderId = useMemo<string>(
    () => cardOrderIds[cardSlug] || getUuid(),
    [cardOrderIds, cardSlug],
  )

  return (
    <CardTile
      card={referencedCard as CompareCredit.FormattedCard}
      title={cardTile.title}
      whyWeLike={whyWeLikeIt}
      key={cardTile._key}
      orderId={orderId}
      categoryId={categoryId}
      externalId={externalId}
      position={position}
    />
  )
}

export const TipCardTileAwardsList = ({
  cardOrderIds,
}: {
  cardOrderIds: Record<string, string>
}) => {
  const TipCardTileAwardsListRender: React.FC<{
    value: {
      cardsAwards: CompareCredit.CardTipAwards[]
      categoryTag: CompareCredit.CategoryTag
      referencedCards: null | Record<string, CompareCredit.Entity>
      sort: string
      tag: CompareCredit.CategoryTag
      title: string
      pinnedCardTips: CompareCredit.PinnedProduct[]
      listClassName?: string
    }
  }> = ({
    value: {
      cardsAwards,
      tag,
      categoryTag,
      sort,
      pinnedCardTips,
      referencedCards,
      listClassName,
    },
  }) => {
    const isPinningActive = useIsPinningActive(sort)

    const userIsOnBrave = useIsUserOnBrave()

    const cardsData: AugmentedCardTile[] = useMemo(() => {
      const augmentedCardTips = cardsAwards.flatMap((cardTile) => {
        const referencedCard = referencedCards?.[cardTile.card.slug]

        if (referencedCard) {
          const parsedSortPosition =
            'sortPosition' in referencedCard
              ? parseInt(referencedCard.sortPosition, 10)
              : undefined

          return [
            {
              ...cardTile,
              referencedCard: referencedCard,
              cardSlug: referencedCard.slug,
              cardIssuer: getIssuer(referencedCard),
              linkText:
                'linkText' in referencedCard
                  ? referencedCard.linkText
                  : undefined,
              sortPosition: !Number.isNaN(parsedSortPosition)
                ? parsedSortPosition
                : 0,
              cardSortContext:
                referencedCard._type === 'card' ||
                referencedCard._type === 'product'
                  ? referencedCard.context?.sort
                  : undefined,
            },
          ]
        }
        return []
      })

      const cardTipsHasCardSortContext = augmentedCardTips.some(
        (item) =>
          item?.cardSortContext &&
          item.cardSortContext.toString().trim() !== '',
      )

      const cardTipsSortedAndFiltered = cardTipsHasCardSortContext
        ? getSortedAndFilteredBySortPosition(augmentedCardTips)
        : augmentedCardTips

      const withBofaExcludedOnBrave = userIsOnBrave
        ? cardTipsSortedAndFiltered.flatMap((cardTip) =>
            cardTip.cardIssuer === 'bank-of-america' ? [] : [cardTip],
          )
        : cardTipsSortedAndFiltered

      const cardTipsWithPinsAppliedIfActive = isPinningActive
        ? pinEntities(
            withBofaExcludedOnBrave,
            formatPinnedProducts(
              pinnedCardTips?.map((pinnedCard) => {
                return {
                  card: {
                    _id: pinnedCard.card._id,
                    slug: pinnedCard.card.slug,
                  },
                  frequencyPercent: pinnedCard.frequencyPercent,
                }
              }) ?? [],
            ),
            mapEntityPositions(cardsAwards.map(({ card: { slug } }) => slug)),
          )
        : withBofaExcludedOnBrave

      return cardTipsWithPinsAppliedIfActive
    }, [
      cardsAwards,
      referencedCards,
      sort,
      userIsOnBrave,
      isPinningActive,
      pinnedCardTips,
    ])

    const arrangementId = useMemo(() => getUuid(), [cardsData])

    /**
     * Fire 'Product List Viewed' Segment event.
     */
    useEffect(() => {
      if (cardsData.length > 0) {
        const cardsInOrder = cardsData.map(
          ({ referencedCard }) => referencedCard,
        )

        viewProductList({
          entities: cardsInOrder,
          category: `tip:${tag.id}`,
          arrangementId: arrangementId,
        })
      }
    }, [cardsData, arrangementId])

    return (
      <ErrorBoundary>
        <div
          className={`c-card-tile-list__container / mb-6 pt-4 ${
            listClassName ? listClassName : ''
          }`}
        >
          {cardsData.length > 0 ? (
            cardsData.map((cardData, idx) => {
              return (
                <TipCardTileDefault
                  key={cardData._key}
                  referencedCards={referencedCards}
                  cardOrderIds={cardOrderIds}
                  cardTile={cardData}
                  tag={tag}
                  categoryTag={categoryTag}
                  arrangementId={arrangementId}
                  position={idx + 1}
                />
              )
            })
          ) : (
            <LoadingSkeleton n={4} />
          )}
        </div>
      </ErrorBoundary>
    )
  }

  return TipCardTileAwardsListRender
}
